import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useAppContext } from '@prisjakt/ui-app-context';
import { useViewportContext } from 'src/components/viewport/context';
import { usePriceRowContext } from '../../../price-row-context';

import { Direction } from '@prisjakt/ui';
import AlternativePriceRow from '../alternative-price-row';
import { ExpandButton } from '../../components/expand-button';
import { StoreInfo } from '../../components/store-info';
import { ReportButton } from '../../components/report-button';
import { trackExpandPriceRowClick } from '../../tracking';
import { fetchSotyAwardsByStoreId } from '../../requests';
import { FeaturedColumns } from './featured-columns';
import { UnfeaturedColumns } from './unfeatured-columns';
import { InfoModal } from '../../components/info-modal';
import { CORNER_BADGE_SIZE } from '../../components/corner-badge';

import { useAuthorisedDealerExperiment } from 'src/hooks/experiments';

import { getTableLinkProps, logRevenueEvent } from '../../utils';

import { Award } from '../../../../../../../types';
import { IRegularPriceRow, ReportRowType } from '../../types';
import { ProductPriceClickStatus } from 'src/areas/product/actions/types';

import * as S from '../styles';

export const RegularPriceRow: React.FC<IRegularPriceRow> = props => {
  const { onClickOutBounce, onPriceClick } = props;
  const [showDisabledInfo, setShowDisabledInfo] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [priceRowReport, setPriceRowReport] = useState(ReportRowType.NONE);
  const [sotyBadge, setSotyBadge] = useState<Award | undefined | null>();
  const [reportRowId, setReportRowId] = useState<string | null>(null);
  const [infoModalOpen, setInfoModalOpen] = useState<boolean>(false);

  // Experiment on showing Authorised Dealer badge
  const [
    showNewAuthorisedDealerBadge,
    setShowNewAuthorisedDealerBadge,
  ] = useState(false);
  const authorisedDealerExperiment = useAuthorisedDealerExperiment();

  const priceRowRef = useRef<HTMLDivElement>(null);
  const { t } = useAppContext();
  const { isMobile } = useViewportContext();
  const {
    productId,
    productName,
    offer,
    showExpandButton,
  } = usePriceRowContext();
  const {
    price,
    priceId,
    store,
    featured,
    stock,
    externalUri,
    alternativePrices,
  } = offer;

  useEffect(() => {
    setShowNewAuthorisedDealerBadge(authorisedDealerExperiment);
  }, [authorisedDealerExperiment]);

  const startBounceTimer = useCallback(
    (id: string) => {
      // Start a timer when a user clicks out on a product
      // if they return to Prisjakt within 15 seconds
      // display an error report button in the price row.

      const startTime = Date.now();

      const onRefocus = () => {
        if (
          Date.now() - startTime > 15000 &&
          document.visibilityState === 'visible'
        ) {
          document.removeEventListener('visibilitychange', onRefocus);
          return;
        }

        if (document.visibilityState === 'visible') {
          setReportRowId(id);

          // Action event for tracking
          onClickOutBounce(productId, priceId);

          document.removeEventListener('visibilitychange', onRefocus);
        }
      };

      document.addEventListener('visibilitychange', onRefocus);
    },
    [onClickOutBounce, priceId, productId, setReportRowId]
  );

  const handleClickOut = useCallback(
    (e, id, isAlternative = false) => {
      const isInternational = offer?.store?.market === 'secondary';
      const status = getGAStatus(isInternational, isAlternative);

      setPriceRowReport(
        isAlternative ? ReportRowType.ALTERNATIVE : ReportRowType.DEFAULT
      );
      if (featured) {
        logRevenueEvent(id, productId);
      }

      if (externalUri) {
        if (IS_BROWSER) {
          startBounceTimer(id);
        }

        if (!showDisabledInfo && !e.defaultPrevented) {
          onPriceClick(featured, undefined, status, isAlternative);
        }
      } else {
        e.preventDefault();
        setShowDisabledInfo(!showDisabledInfo);
      }
    },
    [
      featured,
      externalUri,
      offer,
      productId,
      showDisabledInfo,
      startBounceTimer,
      onPriceClick,
    ]
  );
  const tableLinkProps = getTableLinkProps(
    featured,
    (e: React.MouseEvent) => handleClickOut(e, priceId),
    externalUri
  );

  const handleExpandButtonClick = () => {
    if (isMobile) {
      setInfoModalOpen(state => !state);
    } else {
      setExpanded(!expanded);
    }

    if (!infoModalOpen && !expanded) {
      trackExpandPriceRowClick(productId, productName, store?.id, store?.name);

      if (sotyBadge === undefined && store?.id) {
        fetchSotyAwardsByStoreId(store.id).then(response => {
          if (response?.data?.store?.storeOfTheYearAwards?.length) {
            setSotyBadge(response.data.store.storeOfTheYearAwards[0]);
          } else {
            setSotyBadge(null);
          }
        });
      }
    }
  };

  const handleClickOutAltPrice = useCallback(
    (e, id) => {
      handleClickOut(e, id, true);
    },
    [handleClickOut]
  );

  const closeReportOverlay = useCallback(() => {
    setPriceRowReport(ReportRowType.NONE);
    setReportRowId(null);
  }, [setReportRowId]);

  return (
    <React.Fragment key={store?.id || 'price-row'}>
      <S.RowCard expanded={expanded} featured={featured}>
        <S.Row
          className="pj-ui-price-row" // Used to track click out (see out.js)
          featured={featured}
          noExternalUri={!externalUri}
          showReport={reportRowId === priceId}
          data-test="PriceRow"
          ref={priceRowRef}
          expanded={expanded}
        >
          {showExpandButton ? (
            <S.LeftColumn onClick={handleExpandButtonClick}>
              {offer.authorizedDealer && showNewAuthorisedDealerBadge ? (
                <S.CornerBadgeTooltip
                  placement={Direction.Right}
                  title={t('store', 'brand.recommended-retailer')}
                  $size={CORNER_BADGE_SIZE}
                >
                  <S.CornerBadge />
                </S.CornerBadgeTooltip>
              ) : null}
              <ExpandButton
                expanded={expanded}
                featured={featured}
                title={t('product', 'price.more-info')}
              />
            </S.LeftColumn>
          ) : (
            <S.LeftColumn />
          )}

          {featured && externalUri && (
            <FeaturedColumns tableLinkProps={tableLinkProps} />
          )}

          {(!featured || !externalUri) && (
            <UnfeaturedColumns
              tableLinkProps={tableLinkProps}
              priceRowRef={priceRowRef}
              showDisabledInfo={showDisabledInfo}
              setShowDisabledInfo={setShowDisabledInfo}
            />
          )}
        </S.Row>

        {infoModalOpen && (
          <InfoModal
            alternativePrices={alternativePrices}
            closeModal={() => setInfoModalOpen(false)}
            closeReportOverlay={closeReportOverlay}
            handleClickOutAltPrice={handleClickOutAltPrice}
            isOpen={infoModalOpen}
            priceRowReport={priceRowReport}
            reportRowId={reportRowId}
            sotyBadge={sotyBadge}
            tableLinkProps={tableLinkProps}
          />
        )}

        {expanded && store && (
          <S.InfoRow data-test="ColumnExpanded">
            <StoreInfo sotyBadge={sotyBadge} />
          </S.InfoRow>
        )}
        {expanded && alternativePrices && (
          <S.AlternativePricesArea featured={featured}>
            <S.AlternativePricesInner featured={featured}>
              {alternativePrices.map(altPrice => {
                if (!altPrice) return null;

                return (
                  <React.Fragment key={altPrice.id || 'alternative-price-row'}>
                    <AlternativePriceRow
                      onClickOut={handleClickOutAltPrice}
                      alternativeOffer={altPrice}
                      tableLinkProps={tableLinkProps}
                    />
                    {priceRowReport === ReportRowType.ALTERNATIVE &&
                      reportRowId === altPrice.id && (
                        <S.AltReportRow>
                          <S.Column>
                            <ReportButton
                              priceId={altPrice.id}
                              priceValue={
                                altPrice.price && altPrice.price.exclShipping
                              }
                              stockStatus={
                                altPrice.stock && altPrice.stock.status
                              }
                              onClose={closeReportOverlay}
                              productName={altPrice.name}
                            />
                          </S.Column>
                        </S.AltReportRow>
                      )}
                  </React.Fragment>
                );
              })}
            </S.AlternativePricesInner>
          </S.AlternativePricesArea>
        )}
      </S.RowCard>
      {priceRowReport === ReportRowType.DEFAULT && reportRowId === priceId && (
        <S.ReportRow>
          <S.Column>
            <ReportButton
              priceId={priceId}
              priceValue={price && price.exclShipping}
              stockStatus={stock && stock.status}
              onClose={closeReportOverlay}
              productName={productName}
            />
          </S.Column>
        </S.ReportRow>
      )}
    </React.Fragment>
  );
};

function getGAStatus(
  isInternational: boolean,
  isAlternative: boolean
): ProductPriceClickStatus {
  if (isInternational) {
    return 'international';
  }
  if (isAlternative) {
    return 'variant';
  }
  return '';
}
